import { ScoreQualityType } from '@magicbrief/server/src/api/models/shapes/trpcshapes';
import { BaseTranslation } from 'src/i18n/i18n-types';
import { DiscoverView } from 'src/pages/LibraryV2/routes/Discover/Discover';
import { LiveStatus, Longevity } from 'src/types/ad';
import type { PlatformAdMediaType } from '@magicbrief/prisma/generated/client2';

const adAssetFormats: Record<PlatformAdMediaType, string> = {
  image: 'Image',
  video: 'Video',
  carousel: 'Carousel',
  mixed: 'Mixed',
};

const discoverViews: Record<DiscoverView, string> = {
  discover: 'Library',
};

const adScoreQuality: Record<ScoreQualityType, string> = {
  unknown: 'Unknown',
  average: 'Average',
  good: 'Good',
  great: 'Great',
  winners: 'Winners',
};

const liveStatus: Record<LiveStatus, string> = {
  live: 'Live',
  unknown: 'Unknown',
  ended: 'Ended',
};

const longevity: Record<Longevity, string> = {
  under_7_days: 'Under 7 Days',
  over_7_days: 'Over 7 Days',
  over_15_days: 'Over 15 Days',
  over_30_days: 'Over 30 Days',
  over_3_months: 'Over 3 Months',
  over_6_months: 'Over 6 Months',
  over_12_months: 'Over 12 Months',
};

export const library: BaseTranslation = {
  inspire: 'Inspire',
  discover: 'Discover',
  discoverViews,
  liveFeed: 'Live Feed',
  packs: 'Ad Collections',
  organisation: 'Created',
  created: 'Our ads',
  savedAds: 'Saved ads',
  myAds: 'My Ads',
  teamAds: 'Team Ads',
  team: 'Team',
  collections: 'Collections',
  collection: 'Collection',
  additionalAssets: 'Additional Assets',
  cta: 'CTA',
  createCollection: 'Create Collection',
  deleteFolder: 'Delete Folder',
  renameFolder: 'Rename Folder',
  collectionName: 'Collection Name',
  viewLiveFeed: 'View Live Feed',
  /** @deprecated in lieu of 'following' */
  brands: 'Brands',
  following: 'Track Brands',
  closeLibraryNavigation: 'Close ad library navigation sidebar',
  numAdsSelected: '{count} {{Ad|Ads}} Selected',
  cards: {
    pinToBoard: 'Pin to Board',
    unpinFromBoard: 'Un-Pin from Board',
    moveToBoard: 'Move to Board',
    subheader: 'View details',
    readMore: 'Read More',
  },
  filters: {
    clear: 'Clear Filters',
    tag: {
      label: 'Tag',
      search: 'Search for tags...',
      none: "There aren't any tags in your organisation.",
      emptySearch: 'No matching tags in your organisation.',
    },
    brand: {
      label: 'Brand',
      search: 'Search for brands...',
      none: "There aren't any saved ads here, so there aren't any brands to list.",
      emptySearch: 'No matching brands.',
    },
    industry: {
      label: 'Industry',
      search: 'Search for industries...',
      emptySearch: 'No matching industries.',
    },
    theme: {
      label: 'Theme',
      search: 'Search for themes...',
      emptySearch: 'No matching themes.',
    },
    platform: 'Platform',
    format: 'Format',
    score: {
      label: 'Performance',
      adScore: 'Performance Estimate',
      unlock: 'Unlock',
    },
    languages: {
      search: 'Search for languages...',
      emptySearch: 'No matching languages.',
    },
    adCopy: 'Ad Copy',
    adScore: 'Performance Estimate',
    headlineCta: 'Headline & CTA',
    runTime: 'Run Time',
  },
  adAssetFormats,
  adScoreQuality,
  liveStatus,
  longevity,
  errors: {
    deleteTagError:
      "We weren't able to delete the tag. We will look into this. The error returned was: {error}",
  },
  deleteTag: 'Delete Tag',
  deleteTagConfirm: 'Do you want to delete the tag "{tag}"?',
  tagDeleted: 'Tag deleted',
  emptyTitle: 'Can’t find what you’re looking for?',
  emptySavedAds:
    'Find and follow any brand that is running ads using our brands tab.',
  emptySearchTitle: 'No results',
  addSomeFavourites: 'Add some followed brands',
  viewBrands: 'View Brands',
  searchBrands: 'Search Followed Brands',
  emptySearch:
    "There aren't any results matching the selected filters. Try reducing the number of filters or search parameters and try again.",
  emptyCollection:
    "Try adding some ads to this collection from the <>Live Feed<>, <>your saved ads<>, or <>your team's ads<>. You can also use the Google Chrome extension to save ads directly to a collection from the <>Facebook Ads Library<> or <>TikTok Creative Center<>.",
  deleteCollectionConfirm:
    'Do you want to delete this collection? Ads saved in this collection will still be available in My Ads and Team Ads.',
  collectionDeleted: 'Collection deleted',
  collectionUpdated: 'Collection updated',
  numAds: '{count} ad{{s}}',
  collectionShareScreenReader: '{collection} sharing privacy setting',
  share: {
    public: { description: 'Anyone with the link can view this collection.' },
    private: {
      description:
        'Only team members or users with access can view this collection.',
    },
  },
  collectionPrivate:
    "This collection is private. You'll need to ask the person who sent you the link to make the collection public.",
  sharedBy: 'Shared by {name}',
  admin: {
    details: 'Details',
    comments: 'Comments',
    admin: 'Admin',
    discoverFeature: 'Featured in Discover',
    themes: 'Themes',
    rating: 'Rating',
  },
  createPlatformAd: {
    title: 'Manual Ad Upload',
    labels: {
      title: 'Brand',
      assets: 'Assets',
      copy: 'Ad Copy (optional)',
    },
    placeholders: {
      title: 'Magic Co',
      copy: 'This is where the magic happens!',
    },
    submit: 'Upload',
    success: 'The ad was created successfully',
  },
  updatePlatformAd: {
    title: 'Edit Ad',
    labels: {
      title: 'Brand',
      assets: 'Assets',
      copy: 'Ad Copy (optional)',
    },
    placeholders: {
      title: 'Magic Co',
      copy: 'This is where the magic happens!',
    },
    submit: 'Save',
    success: 'The ad was updated successfully',
  },
  deletePlatformAd: {
    success: 'The ad was deleted successfully',
    confirm: {
      title: 'Delete ad',
      body: 'Are you sure you want to delete this ad?',
    },
  },
  sidebar: {
    tabs: {
      insights: 'Insights',
    },
  },
  forYou: {
    unlock: {
      title: 'Unlock Your Personalised Feed',
      description:
        'Follow & favorite 5 brands to unlock industry trends and competitor insights',
      estimate: '2 mins to complete',
      suggestionTitle: 'Suggested for you',
      searchBrands: 'Search for brands',
      close: 'Close',
      disclaimer:
        'You’re viewing limited access. Complete setup to access full Discover features for FREE!',
      dismiss: 'Not now',
      noSuggestions: 'No brands to show at the moment. Please try again later.',
      following: 'Following',
      follow: 'Follow',
      setupComplete: 'Personalising your feed...',
    },
    ads: {
      title: 'Latest Ads',
    },
    summary: {
      title: 'Your 14-day Digest',
      description:
        'Spotting trends across {count} brands you follow over the last 14 days',
      trackedBrands: 'Your Tracked Brands',
      creativeVelocity: 'Creative Velocity',
      spotlight: 'Brand Spotlight',
      mediaMix: 'Format Mix Trends',
      noData: 'No data available',
    },
  },
};
